// src/components/compliance/documentScanner.ts
/**
 * The scanner of the ID capture, seen from the main thread (Issues #1117, #1141).
 *
 * The camera should behave like a scanner: find the card in the live view and
 * release the shutter by itself as soon as it lies still and fills the frame.
 * Nothing leaves the browser — the detection runs on the local picture, only
 * the accepted capture is uploaded.
 *
 * **OpenCV never touches the main thread.** The Emscripten build is ~10 MB with
 * the WASM embedded as base64; decoding and compiling it froze the page for
 * 15.8 s on a six-year-old phone — «Scanner wird geladen …» stood still and
 * even the shutter button did not answer (Issue #1141). OpenCV therefore lives
 * entirely in {@link ./documentScanner.worker documentScanner.worker.ts}, and
 * this module only holds the thin {@link ScannerClient} that talks to it. A
 * `cv` object must never be imported, held or called here — the grep test in
 * `documentScanner.test.ts` guards that.
 *
 * The geometry and the message shapes live in {@link ./scanGeometry} because
 * the worker needs them too; they are re-exported here so the camera has one
 * address for the whole scanner.
 */
import {
  releaseFrame,
  SCAN_OPENCV_TIMEOUT_MS,
  type Quad,
  type ScanDetection,
  type ScanFrame,
  type ScanImage,
  type ScannerRequest,
  type ScannerResponse,
} from './scanGeometry'

export * from './scanGeometry'

/**
 * The scanner as the camera sees it: a promise whether it came up, and two
 * calls that hand a frame over to the worker.
 *
 * Every call answers `null` instead of throwing. A scanner that fails has to
 * leave the capture exactly as it is without one — live view, shutter button,
 * rectangular crop.
 */
export interface ScannerClient {
  /** `true` once OpenCV runs in the worker, `false` if it never will. */
  readonly ready: Promise<boolean>
  /** The card in `frame`; the frame is consumed (transferred) either way. */
  detect(frame: ScanFrame): Promise<ScanDetection | null>
  /** `frame` straightened onto ID-1 along `quad`; the frame is kept. */
  warp(frame: ScanFrame, quad: Quad): Promise<ScanImage | null>
  /** Stops the worker — pending calls answer `null`. */
  dispose(): void
}

/** What may travel with the message instead of being copied. */
function transferables(frame: ScanFrame): Transferable[] {
  return 'data' in frame ? [frame.data.buffer] : [frame]
}

/**
 * Starts the scanner worker and returns the client for it.
 *
 * The timeout runs from **here**, not from the end of the import: on a slow
 * phone the import alone takes eight seconds, and a deadline that only starts
 * afterwards would never protect anybody (Issue #1141).
 *
 * Nothing about this throws. A browser without workers, a blocked script or a
 * worker that dies on the first message all end in `ready === false`, and the
 * camera keeps the manual shutter exactly as it works without a scanner.
 */
export function createScannerClient(timeoutMs: number = SCAN_OPENCV_TIMEOUT_MS): ScannerClient {
  let worker: Worker | null = null
  try {
    worker = new Worker(new URL('./documentScanner.worker.ts', import.meta.url), {
      type: 'module',
    })
  } catch {
    // No worker (old browser, blocked script) — the scanner stays off
    worker = null
  }

  let ready = false
  let stopped = false
  /** At most one detection is under way; a second would only queue up. */
  let detecting = false
  let nextId = 1
  const pending = new Map<number, (response: ScannerResponse | null) => void>()

  let settleReady: (value: boolean) => void = () => {}
  const readyPromise = new Promise<boolean>((resolve) => {
    settleReady = resolve
  })

  function stop(started: boolean): void {
    if (stopped) return
    stopped = true
    clearTimeout(timer)
    const open = [...pending.values()]
    pending.clear()
    open.forEach((resolve) => resolve(null))
    try {
      worker?.terminate()
    } catch {
      // Already gone
    }
    worker = null
    settleReady(started)
  }

  const timer = setTimeout(() => {
    // Nothing from the worker in time — the scanner stays off, the shutter does not
    if (!ready) stop(false)
  }, timeoutMs)

  if (worker) {
    worker.onmessage = (event: MessageEvent<ScannerResponse>) => {
      const response = event.data
      if (response?.type === 'ready') {
        ready = true
        clearTimeout(timer)
        settleReady(true)
        return
      }
      if (!response || response.type === 'failed') {
        stop(false)
        return
      }
      const resolve = pending.get(response.id)
      if (!resolve) return
      pending.delete(response.id)
      resolve(response)
    }
    // A worker that dies (out of memory, blocked WASM) must not hang the camera
    worker.onerror = () => stop(false)
    worker.onmessageerror = () => stop(false)
  } else {
    clearTimeout(timer)
    settleReady(false)
  }

  /** A request without its id — the client numbers the calls itself. */
  type ScannerCall =
    | { type: 'detect'; frame: ScanFrame }
    | { type: 'warp'; frame: ScanFrame; quad: Quad }

  function send(request: ScannerCall, transfer: Transferable[]): Promise<ScannerResponse | null> {
    const current = worker
    if (!current || !ready || stopped) return Promise.resolve(null)

    const id = nextId
    nextId += 1
    return new Promise<ScannerResponse | null>((resolve) => {
      pending.set(id, resolve)
      try {
        current.postMessage({ ...request, id } as ScannerRequest, transfer)
      } catch {
        pending.delete(id)
        resolve(null)
      }
    })
  }

  return {
    ready: readyPromise,
    async detect(frame: ScanFrame): Promise<ScanDetection | null> {
      if (detecting || !ready || stopped) {
        // One picture at a time: a queue would only deliver outdated outlines
        releaseFrame(frame)
        return null
      }
      detecting = true
      try {
        const response = await send({ type: 'detect', frame }, transferables(frame))
        return response?.type === 'quad' ? response.detection : null
      } finally {
        detecting = false
      }
    },
    async warp(frame: ScanFrame, quad: Quad): Promise<ScanImage | null> {
      // Deliberately not transferred: the caller still needs the still picture
      // for the rectangular crop if the straightening does not work out.
      const response = await send({ type: 'warp', frame, quad }, [])
      return response?.type === 'warped' ? response.image : null
    },
    dispose(): void {
      stop(ready)
    },
  }
}
